/**
 * Post-onboarding redirect: cross-origin handoff from the marketing landing.
 *
 * The landing (`web.landing.starzero`, a different origin) can't share
 * localStorage with the app, and the magic-link email only carries a code — so a
 * destination chosen before sign-up would otherwise be lost by the time the user
 * lands in the app. The landing therefore drops a cookie on the shared parent
 * domain (`.starzero.ai`), which the app reads on first authenticated load and
 * persists to user settings (durable from there; see `UserStore`).
 *
 * Contract for the landing side (separate repo / PR):
 *   document.cookie =
 *     `${COOKIE_NAME}=${encodeURIComponent(destination)}; domain=.starzero.ai; path=/; max-age=1800; samesite=lax`;
 * where `destination` is either an in-app path (`/library/123`) or an allowed
 * external URL (e.g. a `CHAT_URL` route). Same-browser only — by design.
 */
export const POST_SIGNUP_REDIRECT_COOKIE = 'sz_post_signup_redirect';

/**
 * Read the post-signup redirect cookie and delete it (consume-once). Returns the
 * decoded value, or null if absent. Clears on both the host and the `.starzero.ai`
 * parent domain so it can't be re-read on a later load.
 */
export function readAndClearPostSignupRedirectCookie(): string | null {
  if (typeof document === 'undefined') return null;

  const prefix = `${POST_SIGNUP_REDIRECT_COOKIE}=`;
  const entry = document.cookie.split('; ').find((c) => c.startsWith(prefix));

  // Always attempt to clear, even if not found here, so a stale copy on either
  // domain scope is removed. Cookies are deleted by setting an expired max-age
  // with a matching path/domain.
  const expire = `${POST_SIGNUP_REDIRECT_COOKIE}=; path=/; max-age=0`;
  document.cookie = expire;
  document.cookie = `${expire}; domain=.starzero.ai`;

  if (!entry) return null;

  const raw = entry.slice(prefix.length);
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    value = raw;
  }
  return value.trim() || null;
}
